import { lifetimeAnnualizedReturnTooltip, threeYearAnnualizedReturnTooltip } from '@constants/offering-details';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import { date } from '@filters/shared-filters';
import { formattedPerformancePercentageValue } from './performance-portfolio';
import { Fund } from 'types/funds';
import { FundKeyStatistic } from 'types/reit-details';
import { growthTechReitId } from '@constants/reit';
import { Link } from 'types/layout';

export function buildOfferingDetailsLink(offeringId: string, options: Omit<Link, 'router' | 'params'> = {}): Link {
	return { router: 'view-offering-details', params: { offeringId }, ...options };
}

export function buildFundKeyStats(fund: Fund): Array<FundKeyStatistic> {
	const statistics: Array<FundKeyStatistic> = [];

	const annualizedReturnPercentage = fund.annualizedReturns?.annualizedReturnPercentage
		? formattedPerformancePercentageValue(fund.annualizedReturns.annualizedReturnPercentage, true, true)
		: '';

	if (annualizedReturnPercentage) {
		let annualizedReturnTooltipText = '';
		if (fund.annualizedReturns?.annualizationLabelEnum === 'LIFETIME') {
			annualizedReturnTooltipText = lifetimeAnnualizedReturnTooltip;
		} else if (fund.annualizedReturns?.annualizationLabelEnum === 'THREE_YEAR') {
			annualizedReturnTooltipText = threeYearAnnualizedReturnTooltip;
		}

		statistics.push({
			value: annualizedReturnPercentage,
			label: fund.annualizedReturns?.annualizationLabelValue ?? '',
			tooltipText: annualizedReturnTooltipText
		});
	}

	const netAssetValue = fund.fundAum;
	if (netAssetValue) {
		const netAssetsAsOfDate = date(fund.fundAumUpdateDate ?? '', CustomSpacetimeFormat.MONTH_SHORT_DATE_YEAR);

		let tooltipText = "The net value of the fund's assets less its liabilities";
		tooltipText += netAssetsAsOfDate ? ` as of ${netAssetsAsOfDate}.` : '.';

		statistics.push({
			value: netAssetValue,
			label: 'Net asset value (NAV)',
			tooltipText
		});
	}

	if (fund.approxAnnualizedDividendPercentage) {
		statistics.push({
			value: `${fund.approxAnnualizedDividendPercentage}%`,
			label: 'Annualized distribution rate',
			tooltipText:
				"Current month's distribution is annualized and divided by the prior month's net asset value per share.",
			footnote: fund.fundriseId === growthTechReitId ? '1' : undefined
		});
	}

	return statistics;
}
